import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../utils/constants';
import { getAuthHeaders } from '../utils/api';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Загружаем громкость из localStorage при инициализации (даже без аккаунта)
  const [volume, setVolume] = useState(() => {
    const savedVolume = localStorage.getItem('player_volume');
    return savedVolume !== null ? parseFloat(savedVolume) : 0.5;
  });
  const [volumeLoaded, setVolumeLoaded] = useState(false);
  const [loading, setLoading] = useState(true);

  // Загрузка информации о пользователе при монтировании
  useEffect(() => {
    // Всегда загружаем громкость из localStorage (даже без аккаунта)
    const savedVolume = localStorage.getItem('player_volume');
    if (savedVolume !== null) {
      const volumeValue = parseFloat(savedVolume);
      if (!isNaN(volumeValue) && volumeValue >= 0 && volumeValue <= 1) {
        setVolume(volumeValue);
      }
    }
    setVolumeLoaded(true);
    
    const headers = getAuthHeaders();
    if (headers.Authorization) {
      loadUserInfo();
      loadUserSettings();
    } else {
      setLoading(false);
    }
  }, []);

  // Загрузка информации о пользователе
  const loadUserInfo = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers.Authorization) return;

      const response = await axios.get(`${API_URL}/api/auth/me`, { headers });

      if (response.data) {
        setUser(response.data);
        // Сохраняем в localStorage для совместимости
        localStorage.setItem('user', JSON.stringify(response.data));
      }
    } catch (error) {
      console.log('[UserContext] Не удалось загрузить информацию о пользователе:', error);
      // Очищаем токен если он невалидный
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
      }
    } finally {
      setLoading(false);
    }
  };

  // Загрузка настроек пользователя (громкость и т.д.)
  const loadUserSettings = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers.Authorization) {
        // Без аккаунта используем громкость из localStorage (уже загружена)
        setVolumeLoaded(true);
        return;
      }

      const response = await axios.get(`${API_URL}/api/auth/settings`, { headers });

      if (response.data) {
        // Загружаем громкость из БД (приоритет над localStorage)
        if (response.data.volume !== undefined) {
          setVolume(response.data.volume);
          // Сохраняем в localStorage для синхронизации
          localStorage.setItem('player_volume', response.data.volume.toString());
        }
        setVolumeLoaded(true);
      } else {
        setVolumeLoaded(true);
      }
    } catch (error) {
      console.log('[UserContext] Не удалось загрузить настройки:', error);
      // При ошибке используем громкость из localStorage
      setVolumeLoaded(true);
    }
  };

  // Сохранение громкости (в localStorage всегда, в БД только если есть аккаунт)
  const saveVolume = async (newVolume) => {
    try {
      // Всегда сохраняем в localStorage (даже без аккаунта)
      localStorage.setItem('player_volume', newVolume.toString());
      setVolume(newVolume);
      
      // Сохраняем в БД только если есть аккаунт
      const headers = getAuthHeaders();
      if (headers.Authorization && volumeLoaded) {
        console.log(`[UserContext] Сохраняем громкость в БД: ${newVolume}`);
        try {
          const response = await axios.post(
            `${API_URL}/api/auth/volume`,
            { volume: newVolume },
            { headers }
          );

          if (response.data) {
            console.log(`[UserContext] Громкость успешно сохранена в БД: ${newVolume}`);
            return true;
          }
        } catch (error) {
          console.error('[UserContext] Ошибка сохранения громкости в БД:', error.response?.data || error.message);
          // Продолжаем работу даже если не удалось сохранить в БД
        }
      }
      
      return true; // Всегда возвращаем true, так как localStorage сохранен
    } catch (error) {
      console.error('[UserContext] Ошибка сохранения громкости:', error);
      return false;
    }
  };

  // Обновление информации о пользователе
  const updateUser = (userData) => {
    setUser(userData);
    if (userData) {
      localStorage.setItem('user', JSON.stringify(userData));
    }
  };

  // Выход из аккаунта
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    setUser(null);
    // НЕ сбрасываем громкость - она остается в localStorage
    // setVolume остается текущим значением из localStorage
    setVolumeLoaded(false);
  };

  // Проверка авторизации
  const isAuthenticated = () => {
    const headers = getAuthHeaders();
    return !!headers.Authorization && !!user;
  };

  const value = {
    user,
    volume,
    volumeLoaded,
    loading,
    setVolume,
    saveVolume,
    loadUserInfo,
    loadUserSettings,
    updateUser,
    logout,
    isAuthenticated
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

